import { z } from 'zod/v3';
import {
  ActionResult,
  ApplicableContext,
  BaseAction,
  ExecuteContext,
} from '../../actions/framework';
import { ChatModel } from '../chat.model';
import { toast } from '@cotera/client/app/components/ui';
import { err, ok } from 'neverthrow';
import { SubmitToolResultsAction } from './submit-pending-tool-calls.action';
import type { TenantedClient } from '@cotera/client/app/etc/api/typed-client';
import { getToolOrDynamicToolName } from 'ai';

export class ExecuteToolAction extends BaseAction<
  {},
  { t: string; message?: string }[]
> {
  static readonly actionId = 'ExecuteToolAction';
  title = 'Execute Tool';
  inputSchema = z.object({
    toolCallId: z.string(),
    approve: z.boolean().optional(),
    deny: z.boolean().optional(),
    reason: z.string().optional(),
    /**
     * Optional conversation ID override.
     * Use this for sub-agent tools where the conversation ID differs from the targeted model.
     */
    conversationId: z.string().optional(),
  });
  group = 'Chat';

  constructor(
    private readonly client: Pick<TenantedClient['tools'], 'execute'>
  ) {
    super();
  }

  applicable(context: ApplicableContext): boolean {
    return context.hasTargetedModelOfType(ChatModel);
  }

  async execute(
    params: {
      toolCallId: string;
      approve?: boolean;
      deny?: boolean;
      reason?: string;
      conversationId?: string;
    },
    context: ExecuteContext
  ): Promise<ActionResult<{ t: string; message?: string }[]>> {
    const model = context.targetedModelOfType(ChatModel)!;

    const toolCalls = model.messages
      .filter((x) => (x.toolInvocations?.all().length ?? 0) > 0)
      .map((x) => x.toolInvocations!.all())
      .flat();

    const toolCall = toolCalls.find((x) => x.toolCallId === params.toolCallId);

    if (!toolCall) {
      toast.error(`Tool call ${params.toolCallId} not found`);
      return err([{ t: 'error', message: 'Tool call not found' }]);
    }

    // v5: Extract tool name from the tool part
    const toolName = getToolOrDynamicToolName(toolCall as any);

    // Use provided conversation ID (for sub-agent tools) or fall back to model.id
    const conversationId = params.conversationId ?? model.id;

    if (params.deny === true) {
      const reason = params.reason ?? 'User denied tool execution';
      model.addToolResult(params.toolCallId, {
        type: 'error',
        message: reason,
      });

      await context
        .getAction(SubmitToolResultsAction)!
        .askOrExecute({ conversationId }, context);

      return ok({});
    }

    const toolToast = toast.loading(`Executing tool ${toolName}...`);

    const result = await this.client.execute({
      toolName,
      params: toolCall.input,
      conversationId,
      toolCallId: toolCall.toolCallId,
    });

    if (result.isErr()) {
      toolToast.error(JSON.stringify(result.error));
      model.addToolResult(params.toolCallId, {
        type: 'error',
        message: result.error,
      });
      await context
        .getAction(SubmitToolResultsAction)!
        .askOrExecute({ conversationId }, context);

      return err([{ t: 'error', message: JSON.stringify(result.error) }]);
    }

    model.addToolResult(params.toolCallId, result.value.data);

    toolToast.success(`Tool ${toolName} executed successfully`);

    //we exit early as we are waiting on other approvals
    // if (model.hasIncompleteToolCalls()) {
    //   return ok({})
    // }

    await context
      .getAction(SubmitToolResultsAction)!
      .askOrExecute({ conversationId }, context);

    return ok({});
  }
}
